import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Button, Checkbox, Col, Collapse, DatePicker, Form, Input, message, Modal, Row, Select } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { FormInstance } from 'antd/lib/form';
import { Dependent } from '../../interfaces/Dependent';
import { State } from '../../interfaces/State';
import { RelationshipType } from '../../interfaces/RelationshipType';
import personalDataRepo from '../../services/repository/personalData';
import RowSection from '../atoms/RowSection';
import jsonSex from '../../mocks/sex.json';
import { validateCPF } from '../../services/util/validator';
import jsonMaritalStatus from '../../mocks/maritalStatus.json';
import { City } from '../../interfaces/City';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import { isScreenUnderWidth } from '../../services/util/screen';

dayjs.extend(weekday);
dayjs.extend(localeData);

const { Panel } = Collapse;
const { confirm } = Modal;
const { Option } = Select;


const ButtonCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;

type Props = {
  handleNext: () => void
  initForm: Dependent[] | undefined,

}

const Dependents = React.forwardRef<FormInstance, Props>(({ handleNext, initForm }, ref) => {
  const [form] = Form.useForm();

  // @ts-ignore
  useImperativeHandle(ref, () => ({
    validateFields: () => form.validateFields(),
    getFieldsValue: form.getFieldsValue,
    submit: () => form.submit(),
  }));

  const [data, setData] = useState<{
    cities: { key: string; value: City[]; }[],
    states: State[],
    dependentRelationshipTypes: RelationshipType[],
      }>({ states: [], dependentRelationshipTypes: [], cities: [] });


  const fetchData = async () => {
    try {
      const [stateResponse, dependentRelationshipTypes] = await Promise.all([
        personalDataRepo.findAllStates(),
        personalDataRepo.findAllRelationshipType(),
      ]);

      const tempCities = [];

      //trigger city loading if state data is filled
      if (initForm) {

        for (let i = 0; i < initForm.length; i++) {
          const birthState = initForm[i].birthState;
          const cities = await personalDataRepo.findAllCities(birthState);

          // Verifica se a chave já existe no tempCities
          const existingItemIndex = tempCities.findIndex(item => item.key === birthState);

          if (existingItemIndex === -1) {
            // Se não existe, adiciona ao tempCities
            tempCities.push({ key: birthState, value: cities });
          } else {
            // Se existe, atualiza o valor correspondente sem criar um novo Map
            tempCities[existingItemIndex].value = cities;
          }

        }
      }

      setData({
        states: stateResponse.data,
        dependentRelationshipTypes: dependentRelationshipTypes.data,
        cities: tempCities,
      });
    } catch (error) {
      console.error('Ocorreu um erro ao buscar os dados:', error);
    }
  };


  useEffect(() => {
    fetchData();
  }, []);

  const handleChangeCPF = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    const maskedValue = value
      .replace(/\D/g, '') // Remove caracteres não numéricos
      .replace(/(\d{3})(\d)/, '$1.$2') // Insere o primeiro ponto
      .replace(/(\d{3})(\d)/, '$1.$2') // Insere o segundo ponto
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2'); // Insere o hífen

    form.setFieldsValue({
      field: {
        cpf: maskedValue,
      },
    });
  };

  const fixDates = (initForm: Dependent[] | undefined) => {
    initForm?.map(value => {

      if (value && value.birthDate != null) {
        value.birthDate = dayjs(value.birthDate, 'YYYY-MM-DD');
      }
    });

    return initForm;
  };

  const onChangeState = async (value: string) => {
    const cities = await personalDataRepo.findAllCities(value);
    setData({
      ...data, cities: [ ...data.cities, { key: value, value: cities}],
    });
  };

  const isUnder = isScreenUnderWidth(576);

  return (
    <div>
      <Form
        requiredMark={false}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        form={form}
        name='dynamic_form_complex'
        style={{ maxWidth: 600 }}
        autoComplete='off'
        initialValues={{ items: fixDates(initForm) }}
      >

        <Form.List name='items'>
          {(fields, { add, remove }) => (
            <Collapse accordion>
              {fields.map((field) => (
                <Panel
                  header={`Dependente ${field.name + 1}`}
                  key={field.key}
                  extra={
                    <DeleteOutlined
                      onClick={() => {
                        confirm({
                          title: 'Tem certeza que deseja remover este item?',
                          icon: <ExclamationCircleOutlined />,
                          content: 'Esta ação não pode ser desfeita.',
                          okText: 'Sim',
                          cancelText: 'Cancelar',
                          onOk() {
                            remove(field.name);
                            message.success('Exclusão realizada com sucesso');
                          },
                        });

                      }}
                    />
                  }
                >
                  <RowSection>Dados do dependente</RowSection>

                  <Row gutter={16}>
                    <Col xs={24} sm={24} md={24} lg={24}>
                      <Form.Item name={[field.name, 'name']} label='Nome'>
                        <Input placeholder={'Digite aqui'} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={24}>
                      <Form.Item name={[field.name, 'socialName']} label='Nome Social'>
                        <Input placeholder={'Digite aqui'} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Form.Item
                        name={[field.name, 'birthDate']}
                        label='Data de Nascimento'
                        rules={[{ required: true, message: 'Por favor, insira sua data de nascimento.' }]}
                      >
                        <DatePicker
                          format='DD/MM/YYYY'
                          style={{ width: '100%' }}
                          placeholder={'DD/MM/YYYY'}
                          popupClassName={isUnder ? 'small-date-picker' : ''}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Form.Item
                        name={[field.name, 'birthState']}
                        label='Estado de Nascimento'>
                        <Select
                          placeholder='Selecione uma opção'
                          showSearch
                          optionFilterProp='children'
                          allowClear
                          filterOption={(input, option) => (
                            option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          )}
                          onChange={(value) => onChangeState(value)}
                          popupMatchSelectWidth={false}
                        >
                          {data.states.map((state) => (
                            <Option
                              key={`state-${state.id}`}
                              value={state.id}
                            >
                              {state.description}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Form.Item
                        name={[field.name, 'birthCity']}
                        label='Cidade de Nascimento'
                      >
                        <Select
                          placeholder='Selecione uma opção'
                          showSearch
                          optionFilterProp='children'
                          allowClear
                          filterOption={(input, option) => (
                            option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          )}
                          popupMatchSelectWidth={isUnder}
                          disabled={form.getFieldsValue() && form.getFieldsValue().items && form.getFieldsValue().items[field.key] && !form.getFieldsValue().items[field.key].birthState}
                        >
                          {data.cities.find(city => city.key === form.getFieldsValue().items[field.key]?.birthState)?.value.map(value =>
                            (
                              <Option
                                key={`city-${value.id}`}
                                value={value.id}
                              >
                                {value.description}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Form.Item name={[field.name, 'gender']}
                        label='Sexo'>
                        <Select
                          placeholder='Selecione uma opção'
                          showSearch
                          optionFilterProp='children'
                          allowClear
                          filterOption={(input, option) => (
                            option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          )}
                          popupMatchSelectWidth={false}
                        >
                          {jsonSex.map((s) => (
                            <Option
                              key={`gender-${s.id}`}
                              value={s.id}
                            >
                              {s.description}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Form.Item
                        name={[field.name, 'cpf']}
                        label='CPF' rules={[
                          {
                            validator: validateCPF,
                            required:true,
                            min: 0, max: 14,
                          }]}>
                        <Input placeholder='Digite aqui' onChange={handleChangeCPF} maxLength={14} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Form.Item
                        name={[field.name, 'rg']}
                        label='RG'
                      >
                        <Input placeholder='Digite aqui' maxLength={30} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Form.Item name={[field.name, 'relationshipType']} label='Parentesco'>
                        <Select
                          placeholder='Selecione uma opção'
                          showSearch
                          optionFilterProp='children'
                          allowClear
                          filterOption={(input, option) => (
                            option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          )}
                          popupMatchSelectWidth={false}
                        >
                          {data.dependentRelationshipTypes.map((relationshipType) => (
                            <Option
                              key={`state-${relationshipType.id}`}
                              value={relationshipType.id}
                            >
                              {relationshipType.description}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Form.Item name={[field.name, 'maritalStatus']} label='Estado Civil'>
                        <Select
                          placeholder='Selecione uma opção'
                          showSearch
                          optionFilterProp='children'
                          allowClear
                          filterOption={(input, option) => (
                            option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          )}
                          popupMatchSelectWidth={false}
                        >
                          {jsonMaritalStatus.map((marital) => (
                            <Option
                              key={`marital-status-${marital.id}`}
                              value={marital.id}
                            >
                              {marital.description}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={24} sm={24} md={24} lg={24}>
                      <Form.Item name={[field.name, 'isWorkCapable']} valuePropName='checked'>
                        <Checkbox>Possui incapacidade para o Trabalho</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={24} sm={24} md={24} lg={24}>
                      <Form.Item name={[field.name, 'hasIncomeTax']} valuePropName='checked'>
                        <Checkbox>Imposto de Renda</Checkbox>
                      </Form.Item>

                    </Col>
                  </Row>

                  <Row>
                    <Col xs={24} sm={24} md={24} lg={24}>
                      <Form.Item name={[field.name, 'hasFamilyAllowance']} valuePropName='checked'>
                        <Checkbox>Salário família</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>
              ))}

              <Panel
                collapsible={'icon'}
                key='add'
                showArrow={false}
                header={
                  <div style={{ cursor: 'pointer' }} onClick={() => add()}>
                    <PlusOutlined /> Adicionar Dependente
                  </div>
                }
              />
            </Collapse>
          )}
        </Form.List>
      </Form>


      <Row>
        <ButtonCol span={24}>
          <Form.Item>
            <Button type={'primary'} onClick={() => handleNext()}>
              Prosseguir
            </Button>
          </Form.Item>
        </ButtonCol>
      </Row>
    </div>
  );
});

Dependents.displayName = 'Dependents';

export default Dependents;
